import { createContext, useContext } from "react";
import * as React from "react";
import { Stackmap } from "./Stackmap";

export interface WindowVariablesContextType {
  config: string;
  loginMessage: string;
  currentVersion: string;
  tlcGoogleAnalyticsPrivacyURL: string;
  communityTrackingEnabled: boolean;
  customerTrackingEnabled: boolean;
  contentServerCustomerId: string;
  customerTrackingCode: string;
  communityTrackingCode: string;
  hasAuthorizedEResources: boolean;
  libraryType: string; // this can probably be an enum
  svnRevision: string;
}

declare global {
  interface Window extends WindowVariablesContextType {
    StackMap: Stackmap;
  }
}

export const WindowVariablesContext =
  createContext<WindowVariablesContextType>(null);

// This is a wrapper for globally defined variables to allow for better mocking in tests
export const WindowVariablesProvider = ({
  children,
}: React.PropsWithChildren) => {
  const contextValue = {
    config: window.config,
    loginMessage: window.loginMessage,
    currentVersion: window.currentVersion,
    tlcGoogleAnalyticsPrivacyURL: window.tlcGoogleAnalyticsPrivacyURL,
    communityTrackingEnabled: window.communityTrackingEnabled,
    customerTrackingEnabled: window.customerTrackingEnabled,
    hasAuthorizedEResources: window.hasAuthorizedEResources,
    contentServerCustomerId: window.contentServerCustomerId,
    customerTrackingCode: window.customerTrackingCode,
    communityTrackingCode: window.communityTrackingCode,
    libraryType: window.libraryType,
    svnRevision: window.svnRevision,
  };

  return (
    <WindowVariablesContext.Provider value={contextValue}>
      {children}
    </WindowVariablesContext.Provider>
  );
};

export function useWindowVariables() {
  const context = useContext(WindowVariablesContext);

  if (!context) {
    throw new Error(
      "useWindowVariables must be used within a WindowVariablesProvider",
    );
  }

  return context;
}

interface MockWindowVariablesProviderProps
  extends React.PropsWithChildren,
    Partial<WindowVariablesContextType> {}
export const MockWindowVariablesProvider = ({
  communityTrackingEnabled,
  customerTrackingEnabled,
  config,
  tlcGoogleAnalyticsPrivacyURL,
  currentVersion,
  loginMessage,
  children,
}: MockWindowVariablesProviderProps) => {
  const defaultValue: WindowVariablesContextType = {
    config: config || "",
    loginMessage: loginMessage,
    currentVersion: currentVersion || "",
    tlcGoogleAnalyticsPrivacyURL: tlcGoogleAnalyticsPrivacyURL || "",
    communityTrackingEnabled: communityTrackingEnabled || false,
    customerTrackingEnabled: customerTrackingEnabled || false,
    hasAuthorizedEResources: false,
    contentServerCustomerId: "",
    customerTrackingCode: "",
    communityTrackingCode: "",
    libraryType: "",
    svnRevision: "",
  };

  return (
    <WindowVariablesContext.Provider value={defaultValue}>
      {children}
    </WindowVariablesContext.Provider>
  );
};
